import {
  AfterContentChecked,
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { take } from 'rxjs/operators';
import {
  A_AND_E_LOG_CATEGORY,
  A_AND_E_MAINTENANCE,
  M_AND_R_LOG_CATEGORY,
  M_AND_R_MAINTENANCE,
} from 'src/app/constants/monlog-constants';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import { MonLog } from 'src/app/model/monlogs/mon-log';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MonlogsApiService } from 'src/app/services/api/monlogs-api.service';
import { MonLogsTableService } from 'src/app/services/table/monlogs-table.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import {
  getMonLogHeader,
  hasMonLogWorkTypes,
  isAnAlarm,
} from '../functions/monlog-functions';
import { getUserTimezone } from 'src/app/core/functions/date-functions';
import { ProgressSpinnerComponent } from '../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-mon-log-print-container',
  templateUrl: './mon-log-print-container.component.html',
  styleUrls: ['./mon-log-print-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    ButtonDirective,
    TranslateDirective,
    DatePipe,
    TranslatePipe,
  ],
})
export class MonLogPrintContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy, AfterContentChecked
{
  protected _logAndMessage = inject(LogAndMessageService);
  protected _monLogApi = inject(MonlogsApiService);
  protected _facilityBuilder = inject(FacilityBuilderService);
  protected _monLogTableService = inject(MonLogsTableService);

  monLog: MonLog | null = null;
  isAlarm = false;
  showAEWorkTypes = false;
  showMRWorkTypes = false;
  showSeverity = false;
  showNotam = false;
  workType?: string;
  timezone: string | null = null;

  constructor() {
    super();
  }

  ngOnInit() {
    this.timezone = getUserTimezone();
    this.setupTabClosingSubscription();
    this.initializeMonLog();
  }

  ngOnDestroy(): void {
    if (this.tabClosingSub) {
      this.tabClosingSub.unsubscribe();
    }
  }

  ngAfterContentChecked(): void {
    const activeId = this.id;
    if (
      !isNullOrUndefined(activeId) &&
      this.monLog &&
      this.monLog.id !== activeId &&
      !this.doCheckComplete
    ) {
      this.doCheckComplete = true;
      this._monLogTableService.setSelected(null);
      this.initializeMonLog();
    }
  }

  initializeMonLog() {
    const id = this.id;

    if (!id) {
      this.monLog = this._monLogTableService.getSelected();
      // no id and nothing cached, go back to search.
      if (!this.monLog) {
        this._logAndMessage.errorLogOnly('Mon log was null');
        const index = this._tabService.getActiveIndex();
        this._tabService.closeTab(index);
      }
      this.initialize();
    } else {
      this._monLogApi
        .getMonLogWithAlarms(id)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            if (response) {
              this.monLog = response;
              this.doCheckComplete = false;
              this.initialize();
            } else {
              // nothing found, go back to search.
              this._logAndMessage.errorLogOnly(
                'Could not find mon log with ID: ' + id
              );
              const index = this._tabService.getActiveIndex();
              this._tabService.closeTab(index);
            }
          },
        });
    }
  }

  async initialize() {
    const label = await firstValueFrom(
      this._translateService.get('COMMON.LABEL.PRINT')
    );
    this._tabService.updateActiveTabLabel(
      label + getMonLogHeader(this.monLog!)
    );
    this.checkAndUpdateLogCategory();

    this.isAlarm = isAnAlarm(this.monLog!);

    if (hasMonLogWorkTypes(this.monLog!)) {
      this.workType = this.monLog?.MonLogWorkTypes?.map((t) => t.label).join(
        ', '
      );
    }
  }

  checkAndUpdateLogCategory() {
    // these need checked outside of a new log or existing log.
    if (
      this.monLog?.MonLogTypes?.some(
        (type) => type.label === A_AND_E_MAINTENANCE
      )
    ) {
      this.showAEWorkTypes = true;
      this.monLog.logCategory = A_AND_E_LOG_CATEGORY;
    }

    if (
      this.monLog?.MonLogTypes?.some(
        (type) => type.label === M_AND_R_MAINTENANCE
      )
    ) {
      this.showMRWorkTypes = true;
      this.monLog.logCategory = M_AND_R_LOG_CATEGORY;
    }
  }

  print() {
    // This used to use printjs, but we ran into some issues with lines
    // overlapping each other in the migration to angular 13, so now
    // we use @media print styling to only show what is needed
    // and use the default window.print() to generate the document!
    window.print();
  }
}
